import React from 'react';
import { motion } from 'framer-motion';

const MentalHealthAwareness = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-neutral-900 to-black text-white py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-8 text-gradient">Mental Health and Awareness Program</h1>
          <p className="text-neutral-400 mb-12">Annual Report: August 1, 2024 - July 30, 2025</p>

          <div className="space-y-8 text-neutral-300">
            <section>
              <div className="glass rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Program Overview</h2>
                <p className="leading-relaxed">
                  Our Mental Health and Awareness Program provides comprehensive mental health screening, counseling, therapy, and ongoing support to rural communities in Nepal. Through education, therapy, counseling, home visits, and natural/horticulture therapy, we help individuals improve their mental wellbeing.
                </p>
              </div>
            </section>

            <section>
              <div className="glass rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Key Statistics</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="bg-gradient-to-r from-purple-500/10 to-indigo-500/10 rounded-xl p-6">
                    <div className="text-4xl font-bold text-purple-400 mb-2">423</div>
                    <div className="text-neutral-300">Mental Health Screenings</div>
                    <div className="text-sm text-neutral-400 mt-2">Participants assessed</div>
                  </div>
                  <div className="bg-gradient-to-r from-purple-500/10 to-indigo-500/10 rounded-xl p-6">
                    <div className="text-4xl font-bold text-purple-400 mb-2">154</div>
                    <div className="text-neutral-300">Self-Reported Issues</div>
                    <div className="text-sm text-neutral-400 mt-2">Seeking support</div>
                  </div>
                  <div className="bg-gradient-to-r from-purple-500/10 to-indigo-500/10 rounded-xl p-6">
                    <div className="text-4xl font-bold text-purple-400 mb-2">138</div>
                    <div className="text-neutral-300">Under Medication</div>
                    <div className="text-sm text-neutral-400 mt-2">Receiving treatment</div>
                  </div>
                  <div className="bg-gradient-to-r from-purple-500/10 to-indigo-500/10 rounded-xl p-6">
                    <div className="text-4xl font-bold text-purple-400 mb-2">48</div>
                    <div className="text-neutral-300">Hospital Referrals</div>
                    <div className="text-sm text-neutral-400 mt-2">To Patan Mental Hospital</div>
                  </div>
                  <div className="bg-gradient-to-r from-purple-500/10 to-indigo-500/10 rounded-xl p-6">
                    <div className="text-4xl font-bold text-purple-400 mb-2">412</div>
                    <div className="text-neutral-300">Follow-up Visits</div>
                    <div className="text-sm text-neutral-400 mt-2">Home visits conducted</div>
                  </div>
                  <div className="bg-gradient-to-r from-purple-500/10 to-indigo-500/10 rounded-xl p-6">
                    <div className="text-4xl font-bold text-purple-400 mb-2">90%</div>
                    <div className="text-neutral-300">Improvement Rate</div>
                    <div className="text-sm text-neutral-400 mt-2">Through therapy & support</div>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <div className="glass rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Demographics</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-4">Gender Distribution</h3>
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="text-neutral-300">Women</span>
                          <span className="text-purple-400 font-bold">75%</span>
                        </div>
                        <div className="w-full bg-neutral-700 rounded-full h-3">
                          <div className="h-3 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500" style={{width: '75%'}}></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="text-neutral-300">Men</span>
                          <span className="text-purple-400 font-bold">25%</span>
                        </div>
                        <div className="w-full bg-neutral-700 rounded-full h-3">
                          <div className="h-3 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500" style={{width: '25%'}}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-4">Age Distribution</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between text-neutral-300">
                        <span>46-55 years (Highest)</span>
                        <span className="text-purple-400 font-bold">31%</span>
                      </div>
                      <div className="text-sm text-neutral-400">
                        Most participants were middle-aged adults
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <div className="glass rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Program Components</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    {
                      title: "Mental Health Screening",
                      description: "Comprehensive assessments to identify mental health issues and needs within the community.",
                      icon: (
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"></path>
                        </svg>
                      )
                    },
                    {
                      title: "Counseling & Therapy",
                      description: "Regular counseling sessions and therapeutic interventions by trained professionals.",
                      icon: (
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                        </svg>
                      )
                    },
                    {
                      title: "Hospital Referrals",
                      description: "Specialized assessment and treatment at Patan Mental Hospital with ongoing follow-up support.",
                      icon: (
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                        </svg>
                      )
                    },
                    {
                      title: "Home Visits & Follow-ups",
                      description: "Regular home visits and follow-ups for the most needy patients to ensure continuous care.",
                      icon: (
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
                        </svg>
                      )
                    },
                    {
                      title: "Natural & Horticulture Therapy",
                      description: "Therapeutic gardening and nature-based interventions showing 90% improvement rate.",
                      icon: (
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                        </svg>
                      )
                    },
                    {
                      title: "Community Education",
                      description: "Awareness programs and education to reduce stigma and promote mental health literacy.",
                      icon: (
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                        </svg>
                      )
                    }
                  ].map((component, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                      className="flex items-start space-x-4 p-6 bg-gradient-to-r from-neutral-800/50 to-neutral-900/50 rounded-xl hover:scale-105 transition-transform duration-300"
                    >
                      <div className="text-purple-400 mt-1">{component.icon}</div>
                      <div>
                        <h3 className="text-lg font-bold text-white mb-2">{component.title}</h3>
                        <p className="text-neutral-300 text-sm">{component.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>

            <section>
              <div className="glass rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Key Findings</h2>
                <div className="space-y-4">
                  <div className="p-6 bg-gradient-to-r from-neutral-800/50 to-neutral-900/50 rounded-xl">
                    <div className="flex items-start space-x-4">
                      <div className="text-purple-400 text-2xl font-bold">1.</div>
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-2">Depression is the Most Common Issue</h3>
                        <p className="text-neutral-300">Depression was identified as the highest reported mental health condition among participants.</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6 bg-gradient-to-r from-neutral-800/50 to-neutral-900/50 rounded-xl">
                    <div className="flex items-start space-x-4">
                      <div className="text-purple-400 text-2xl font-bold">2.</div>
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-2">Women Most Affected</h3>
                        <p className="text-neutral-300">75% of participants seeking mental health support were women, highlighting the need for gender-sensitive interventions.</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-6 bg-gradient-to-r from-neutral-800/50 to-neutral-900/50 rounded-xl">
                    <div className="flex items-start space-x-4">
                      <div className="text-purple-400 text-2xl font-bold">3.</div>
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-2">High Success Rate</h3>
                        <p className="text-neutral-300">90% of participants showed improvement through natural and horticulture therapy combined with regular follow-up and counseling.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <div className="glass rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Annual Report</h2>
                <p className="leading-relaxed mb-6">
                  For comprehensive details on program activities, survey results, methodology, and impact analysis, please download the complete annual report below.
                </p>
                <a
                  href="/images/Annual Report -Mental Health and Awarness program 2024-2025.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg btn-shine transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/25"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                  <span>Download Annual Report (PDF)</span>
                </a>
              </div>
            </section>

            <section>
              <div className="glass rounded-2xl p-8 text-center">
                <h2 className="text-2xl font-bold text-white mb-4">Support This Program</h2>
                <p className="text-neutral-300 mb-6">
                  Your donation helps provide mental health support and counseling to rural communities in Nepal.
                </p>
                <a
                  href="/#donate"
                  className="inline-block px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-lg btn-shine transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/25"
                >
                  Donate Now
                </a>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default MentalHealthAwareness;

